<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4 p-2">
    <!-- Product Registration Selection/Header -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-0 shrink-0 border-b border-slate-100">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <h2 class="text-base font-bold text-[#0d4169] text-left m-0 shrink-0">{{ registrationTitle }}</h2>
          <div class="flex gap-6 -mb-[1px] w-full lg:w-auto">
            <button
              type="button"
              @click="selectRegistrationTab('product')"
              :class="{
                'border-[#0d4169] text-[#0d4169]': activeRegistrationTab === 'product',
                'border-transparent text-slate-500': activeRegistrationTab !== 'product',
              }"
              class="pb-2 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors"
            >
              Product
            </button>
            <button
              type="button"
              @click="selectRegistrationTab('debone')"
              :class="{
                'border-[#0d4169] text-[#0d4169]': activeRegistrationTab === 'debone',
                'border-transparent text-slate-500': activeRegistrationTab !== 'debone',
              }"
              class="pb-2 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors"
            >
              Debone
            </button>
          </div>
        </div>
      </div>

      <form class="px-4 sm:px-6 pb-5 pt-2 shrink-0" @submit.prevent="handleSubmit">
        <div class="flex flex-wrap gap-x-3 sm:gap-x-10 gap-y-3 w-full">
          <!-- Department -->
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Department</label>
            <select v-model="form.departmentId" class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]" @change="handleDepartmentChange">
              <option value="">All Departments</option>
              <option v-for="dep in departments" :key="dep.id" :value="String(dep.id)">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <!-- Sub Department -->
          <div v-if="activeRegistrationTab === 'product'" class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Sub Department</label>
            <select v-model="form.subDepartmentId" class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]" :disabled="subDepartments.length === 0">
              <option value="" disabled>Select Sub Dept</option>
              <option v-for="sd in subDepartments" :key="sd.id" :value="String(sd.id)">
                {{ sd.name }}
              </option>
            </select>
          </div>

          <!-- Select Item (Debone only) -->
          <div v-if="activeRegistrationTab === 'debone'" class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Select Item</label>
            <select
              v-model="form.itemId"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
            >
              <option value="">Select Item</option>
              <option v-for="item in activeItemsForDebone" :key="item.id" :value="String(item.id)">
                {{ item.name }}
              </option>
            </select>
          </div>

          <!-- Product Name / Debone Name -->
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">{{ nameFieldLabel }}</label>
            <input v-model="form.productName" type="text" class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs h-[34px]" :placeholder="nameFieldPlaceholder">
          </div>

          <!-- U.O.M -->
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">U.O.M</label>
            <select v-model="form.uom" class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]" @change="handleRegistrationUomChange">
              <option value="" disabled selected>Select U.O.M</option>
              <option v-for="opt in registrationUomOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
              <!-- <option value="Portion">Portion</option> -->
            </select>
          </div>

          <!-- Quantity -->
          <div v-if="showRegistrationQuantity" class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Quantity</label>
            <input v-model.number="form.quantity" type="number" step="0.001" inputmode="decimal" class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs h-[34px]" placeholder="0.000">
          </div>

          <!-- Action Button -->
          <div class="flex items-end pb-[1px] w-full sm:w-auto">
            <button
              type="submit"
              :disabled="!canSubmitRegistration"
              class="w-full sm:w-auto px-10 py-1.5 bg-[#0d4169] text-white text-[0.7rem] font-bold rounded shadow-sm transition-all h-[34px]"
              :class="canSubmitRegistration ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
            >
              {{ editingIndex !== null ? 'Update' : 'Register' }}
            </button>
          </div>
          <div class="flex flex-col gap-1 w-full sm:w-[260px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Search By Name</label>
            <input
              v-model="productSearchQuery"
              type="text"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white h-[34px]"
              :placeholder="searchPlaceholder"
            >
          </div>
        </div>
      </form>

      <div class="px-4 pb-4 pt-2 overflow-x-auto custom-scrollbar">
        <div v-if="productCostsLoading && activeRegistrationTab === 'product'" class="flex items-center justify-center py-8">
          <div class="h-5 w-5 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></div>
        </div>
        <table v-else class="product-table border-collapse bg-white w-full">
          <thead>
            <tr>
              <th>No.</th>
              <th>Department</th>
              <th v-if="activeRegistrationTab === 'product'">Sub Dept</th>
              <th>U.O.M</th>
              <th v-if="activeRegistrationTab !== 'debone'">Qty</th>
              <th>{{ activeRegistrationTab === 'debone' ? 'Debone Name' : 'Product Name' }}</th>
              <th v-if="activeRegistrationTab === 'debone'">Item Name</th>
              <th>Unit Cost</th>
              <th v-if="activeRegistrationTab === 'product'">Total Cost</th>
              <th v-if="activeRegistrationTab === 'product'">Contingency</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableRows" :key="row.id || index" class="hover:bg-slate-50 border-b border-slate-100">
              <td class="px-4 py-2 text-center text-xs text-slate-500">{{ index + 1 }}</td>
              <td class="px-4 py-2 text-slate-700">{{ row.department }}</td>
              <td v-if="activeRegistrationTab === 'product'" class="text-slate-700 px-4 py-2">{{ row.subDepartment }}</td>
              <td class="text-center text-slate-600 px-4 py-2">{{ row.uom }}</td>
              <td v-if="activeRegistrationTab !== 'debone'" class="text-center px-4 py-2">{{ formatRegistrationTableQty(row) }}</td>
              <td class="px-4 py-2 text-xs">
                <a
                  v-if="activeRegistrationTab === 'product'"
                  @click="openRecipeModal(row)"
                  class="text-blue-500 font-bold cursor-pointer underline hover:text-blue-700"
                >
                  <u>{{ row.productName }}</u>
                </a>
                <span v-else class="text-slate-700 font-semibold">{{ row.productName }}</span>
              </td>
              <td v-if="activeRegistrationTab === 'debone'" class="px-4 py-2 text-slate-700">{{ row.itemName || '-' }}</td>
              <td class="text-right text-slate-600 px-4 py-2">{{ row.unitCost == null ? '' : money(row.unitCost) }}</td>
              <td v-if="activeRegistrationTab === 'product'" class="text-right font-bold text-[#0d4169] px-4 py-2">{{ row.unitCost == null ? '' : money(row.unitCost * row.quantity) }}</td>
              <td v-if="activeRegistrationTab === 'product'" class="text-center px-4 py-2">
                <a
                  @click="openContingencyModal(row)"
                  class="text-blue-500 underline"
                  :class="canEditProduct ? 'cursor-pointer hover:text-blue-700' : 'opacity-50 cursor-not-allowed'"
                >
                  {{ row.contingency ?? DEFAULT_PRODUCT_CONTINGENCY }}
                </a>
              </td>
              <td class="text-center px-4 py-2">
                <span :class="row.active ? 'status-badge active' : 'status-badge inactive'">{{ row.active ? 'Active' : 'Inactive' }}</span>
              </td>
              <td class="text-center px-4 py-2">
                <div class="flex items-center justify-center gap-3">
                  <a
                    @click="handleEdit(index)"
                    class="text-blue-500 text-xs font-bold"
                    :class="canEditProduct ? 'cursor-pointer hover:underline' : 'opacity-50 cursor-not-allowed'"
                  >Edit</a>
                  <label
                    class="toggle-switch"
                    :title="row.active ? 'Deactivate' : 'Activate'"
                    :class="{ 'cursor-not-allowed opacity-50 action-disabled-marker': !canEditProduct }"
                  >
                    <input type="checkbox" :checked="row.active" :disabled="!canEditProduct" @change="toggleProductStatus(row)">
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Recipe Modal -->
    <div v-if="showModal" class="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <!-- Modal Header -->
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between shrink-0">
          <h3 class="text-sm font-bold tracking-wide">
            Recipe for: <span class="text-white/90">{{ selectedProduct?.productName }}</span>
          </h3>
          <button @click="closeModalWithSave" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white transition-all text-2xl">&times;</button>
        </div>
        
        <div class="p-6 overflow-y-auto custom-scrollbar">

        <div v-if="recipeLoading" class="flex items-center justify-center py-12">
          <div class="h-5 w-5 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></div>
        </div>

        <div v-else>

        <form class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 items-end" @submit.prevent="saveIngredient">
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Add Ingredient</label>
            <select v-model="addIngredientItemId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer h-[34px]" @change="onIngredientSelect">
              <option value="">Choose Ingredient</option>
              <option v-for="opt in ingredientOptions" :key="opt.type + opt.id" :value="opt.type + ':' + opt.id" :class="{ 'bg-emerald-100 font-bold': opt.type === 'product' }">
                {{ getIngredientOptionLabel(opt) }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Quantity</label>
            <input v-model.number="addQuantity" type="number" step="0.01" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm h-[34px]" placeholder="0.00">
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Remark</label>
            <input v-model="addRemark" type="text" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm h-[34px]" placeholder="Optional remark">
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-transparent">Action</label>
            <div class="flex items-center gap-2">
              <button
                type="submit"
                :disabled="!canSubmitRecipeIngredient"
                class="w-fit px-4 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md transition-all active:scale-95 h-[34px]"
                :class="canSubmitRecipeIngredient ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
              >
                {{ editingRecipeIndex === -1 ? 'Add' : 'Update' }}
              </button>
              <button
                type="button"
                class="px-4 ml-6 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap h-[34px]"
                :class="canSaveRecipeNotes ? 'hover:bg-emerald-700' : 'opacity-50 cursor-not-allowed'"
                @click="saveProductRecipeNotes"
                :disabled="recipeSaving || !canSaveRecipeNotes"
              >
                <div
                  v-if="recipeSaving"
                  class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
                ></div>
                <span v-if="!recipeSaving">Save</span>
              </button>
            </div>
          </div>
        </form>

        <div class="w-full overflow-x-auto">
          <table class="recipe-table">
            <thead>
              <tr>
                <th>Ingredient Name</th>
                <th>U.O.M</th>
                <th>Quantity</th>
                <th>U.P</th>
                <th>Total Price</th>
                <th>Remark</th>
                <th>Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in recipeRows" :key="idx" class="hover:bg-slate-100/50 transition-colors border-b">
                <td :class="['font-semibold', row.type === 'product' ? 'text-emerald-600' : '']">
                  {{ row.ingredient }}
                </td>
                <td>{{ getRecipeModalDisplayUom(row) }}</td>
                <td>{{ row.qty }}</td>
                <td>{{ money(getRecipeModalDisplayUp(row)) }}</td>
                <td class="font-bold">{{ money(getRecipeModalDisplayTotal(row)) }}</td>
                <td>{{ row.remark }}</td>
                <td class="text-center">
                  <div
                    class="toggle-switch"
                    :class="canEditProduct ? '' : 'cursor-not-allowed opacity-50'"
                    @click="toggleActive(row)"
                  >
                    <span class="toggle-slider" :class="{ 'active': row.isActive }"></span>
                  </div>
                </td>
                <td class="text-slate-500">
                  <a
                    @click="editRecipeRow(idx)"
                    class="text-blue-500 px-2 py-1.5 transition-all active:scale-95"
                    :class="canEditProduct ? 'cursor-pointer hover:underline' : 'opacity-50 cursor-not-allowed'"
                  >Edit</a>
                  <a
                    @click="deleteRecipeRow(idx)"
                    class="text-red-500 px-2 py-1.5 transition-all active:scale-95"
                    :class="canEditProduct ? 'cursor-pointer hover:underline' : 'opacity-50 cursor-not-allowed'"
                  >Delete</a>
                </td>
              </tr>

              <tr class="border-t">
                <td class="font-semibold">Cost Sub Total</td>
                <td class="font-bold">{{ money(costSubTotal) }}</td>
                <td colspan="6"></td>
              </tr>
              <tr>
                <td class="font-semibold">Contingency {{ selectedProduct?.contingency ?? DEFAULT_PRODUCT_CONTINGENCY }}%</td>
                <td class="font-bold">{{ money(contingencyAmount) }}</td>
                <td colspan="6"></td>
              </tr>
              <tr>
                <td class="font-semibold">Total Cost</td>
                <td class="font-bold">{{ money(totalCost) }}</td>
                <td colspan="6"></td>
              </tr>
              <tr>
                <td class="font-semibold">Cost Per Unit</td>
                <td class="font-bold">{{ money(costPerUnit) }}</td>
                <td colspan="6"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex gap-3">
          <div class="flex-1 max-w-[50%]">
            <label class="block text-[0.78rem] font-semibold text-[#1e293b] mb-1 text-left">Remarks / Notes:</label>
            <textarea v-model="recipeNotes" rows="3" class="w-full px-2.5 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm resize-y" placeholder="Enter notes..."></textarea>
          </div>

          <div class="flex items-center justify-center flex-1 gap-3"></div>
        </div>
        </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Contingency Modal -->
  <div v-if="showContingencyModal" class="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
      <div class="px-5 py-3 bg-[#0d4169] text-white flex items-center justify-between">
        <h3 class="text-sm font-bold tracking-wide">Edit Contingency</h3>
        <button @click="closeContingencyModal" class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white transition-all text-xl">&times;</button>
      </div>
      <div class="p-6">
        <h3 class="text-lg font-bold text-[#0d4169] mb-4">Edit Contingency</h3>
        <div class="flex flex-col gap-3">
          <div>
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Contingency Value(%)</label>
            <input
              v-model="contingencyValue"
              type="number"
              step="0.01"
              class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm mt-1"
              placeholder="Enter contingency value"
            >
          </div>
          <div class="flex justify-end gap-3 mt-5">
            <button
              type="button"
              class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all"
              @click="closeContingencyModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="px-4 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all"
              @click="saveContingency"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <Transition name="toast">
    <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm">
      <div :class="[
        'px-4 py-3 rounded-lg shadow-lg text-white font-semibold',
        toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
      ]">
        {{ toast.message }}
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import store from '../../../store'
import { apiFetch, WS_BASE_URL } from '../../../utils/api'

const DEFAULT_PRODUCT_CONTINGENCY = 5

// Product Registration Logic
const activeRegistrationTab = ref('product')
const activeItemsForDebone = ref([])
const items = ref([])
const deboneItems = ref([])
const productSearchQuery = ref('')
const productCostsLoading = ref(false)

const departments = ref([])
const subDepartments = ref([])

let productSyncSocket = null
let productSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
function getPrivilegeBlock(name) {
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}
const productPrivilege = computed(() => getPrivilegeBlock('Product'))
const canAddProduct = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!productPrivilege.value?.Add
})
const canEditProduct = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!productPrivilege.value?.Edit
})
const canSubmitRecipeIngredient = computed(() => (
  editingRecipeIndex.value === -1 ? canAddProduct.value : canEditProduct.value
))
const canSaveRecipeNotes = computed(() => canAddProduct.value || canEditProduct.value)
// Primary department id (first / main department)
const departmentId = computed(() => currentUser.value?.departmentId || currentUser.value?.department_id || currentUser.value?.department?.id || null)
// All departments from token as comma-separated names (e.g. "Kitchen, Bar")
const userDepartmentNames = computed(() => {
  const raw = currentUser.value?.departments
  if (!raw) return []
  return raw
    .toString()
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
})

const form = ref({
  departmentId: '',
  subDepartmentId: '',
  itemId: '',
  productName: '',
  uom: '',
  quantity: ''
})

const editingIndex = ref(null)
/** U.O.M. when registration edit started; used to detect server-side recipe recalc on UOM change. */
const editingRegistrationUom = ref('')
const registrationTitle = computed(() => (
  activeRegistrationTab.value === 'debone' ? 'Debone Registration' : 'Product Registration'
))
const nameFieldLabel = computed(() => (
  activeRegistrationTab.value === 'debone' ? 'Debone Name' : 'Product Name'
))
const nameFieldPlaceholder = computed(() => (
  activeRegistrationTab.value === 'debone' ? 'e.g. Deboned Chicken' : 'e.g. Club Sandwich'
))
const searchPlaceholder = computed(() => (
  activeRegistrationTab.value === 'debone' ? 'Search debone name...' : 'Search product name...'
))
const showRegistrationQuantity = computed(() => {
  // Debone tab never shows Quantity (register or edit).
  if (activeRegistrationTab.value === 'debone') return false
  return true
})
function isDeboneNonPieceUom(uom) {
  const normalized = String(uom || '').trim()
  return normalized === 'K.G.' || normalized === 'liter'
}
// Debone U.O.M always shows all options (not filtered by selected item unit).
const registrationUomOptions = computed(() => [
  { value: 'K.G.', label: 'K.G.' },
  { value: 'liter', label: 'liter' },
  { value: 'Piece', label: 'Pieces' },
])
function formatRegistrationTableQty(row) {
  if (activeRegistrationTab.value === 'debone' && isDeboneNonPieceUom(row?.uom)) {
    return ''
  }
  return row?.quantity ?? ''
}
function handleRegistrationUomChange() {
  if (activeRegistrationTab.value === 'debone' && isDeboneNonPieceUom(form.value.uom)) {
    form.value.quantity = ''
  }
}
function normalizeDepartmentScopedName(value) {
  return String(value || '').trim().toLowerCase()
}
function findDepartmentScopedDuplicateRegistration(rows, { departmentId, name, excludeId = null }) {
  const normalizedDepartmentId = String(departmentId || '').trim()
  const normalizedName = normalizeDepartmentScopedName(name)
  if (!normalizedDepartmentId || !normalizedName) return null

  return (Array.isArray(rows) ? rows : []).find((row) => {
    if (!row) return false
    if (excludeId !== null && Number(row.id) === Number(excludeId)) return false
    return String(row.departmentId || '').trim() === normalizedDepartmentId
      && normalizeDepartmentScopedName(row.productName) === normalizedName
  }) || null
}
function validateRegistrationUniqueness() {
  const name = String(form.value.productName || '').trim()
  const departmentId = String(form.value.departmentId || '').trim()
  if (!name || !departmentId) return true

  const excludeId = editingIndex.value
  const isDeboneTab = activeRegistrationTab.value === 'debone'
  const currentRows = isDeboneTab ? deboneItems.value : items.value
  const otherRows = isDeboneTab ? items.value : deboneItems.value

  const duplicateInCurrentTab = findDepartmentScopedDuplicateRegistration(currentRows, {
    departmentId,
    name,
    excludeId,
  })
  const duplicateInOtherTab = findDepartmentScopedDuplicateRegistration(otherRows, {
    departmentId,
    name,
  })

  if (!duplicateInCurrentTab && !duplicateInOtherTab) return true

  const label = isDeboneTab ? 'Debone name' : 'Product name'
  if (duplicateInOtherTab && !duplicateInCurrentTab) {
    const conflictLabel = isDeboneTab ? 'product' : 'debone'
    showToast(`${label} must be unique per department and cannot match an existing ${conflictLabel} name.`, 'error')
    return false
  }

  showToast(`${label} must be unique per department.`, 'error')
  return false
}
const canSubmitRegistration = computed(() => {
  if (!canAddProduct.value && editingIndex.value === null) return false
  if (editingIndex.value !== null && !canEditProduct.value && !canAddProduct.value) return false
  if (activeRegistrationTab.value === 'debone') {
    if (!form.value.departmentId || !form.value.itemId || !form.value.productName || !form.value.uom) return false
    return true
  }
  return canAddProduct.value
})
const tableRows = computed(() => {
  if (activeRegistrationTab.value === 'debone') return filteredDeboneItems.value
  return filteredItems.value
})
const filteredItems = computed(() => {
  let result = items.value

  const depId = String(form.value.departmentId || '').trim()
  if (depId) {
    result = result.filter((row) => String(row?.departmentId ?? '') === depId)
  }

  const query = String(productSearchQuery.value || '').trim().toLowerCase()
  if (query) {
    result = result.filter((row) =>
      String(row?.productName || '').toLowerCase().includes(query)
    )
  }

  // Keep inactive (status off) at the bottom; preserve existing name order within each group
  return [...result].sort((a, b) => {
    const aOff = a?.active ? 0 : 1
    const bOff = b?.active ? 0 : 1
    if (aOff !== bOff) return aOff - bOff
    return String(a?.productName || '').localeCompare(String(b?.productName || ''), undefined, { sensitivity: 'base' })
  })
})
const filteredDeboneItems = computed(() => {
  let result = deboneItems.value

  const depId = String(form.value.departmentId || '').trim()
  if (depId) {
    result = result.filter((row) => String(row?.departmentId ?? '') === depId)
  }

  const query = String(productSearchQuery.value || '').trim().toLowerCase()
  if (query) {
    result = result.filter((row) =>
      String(row?.productName || '').toLowerCase().includes(query)
      || String(row?.itemName || '').toLowerCase().includes(query)
    )
  }

  return [...result].sort((a, b) => {
    // Keep inactive (status off) at the bottom without changing name sort among peers
    const aOff = a?.active ? 0 : 1
    const bOff = b?.active ? 0 : 1
    if (aOff !== bOff) return aOff - bOff
    return String(a?.itemName || '').localeCompare(String(b?.itemName || ''), undefined, { sensitivity: 'base' })
      || String(a?.productName || '').localeCompare(String(b?.productName || ''), undefined, { sensitivity: 'base' })
  })
})

async function loadActiveItemsForDebone() {
  if (!companyId.value && !branchId.value) {
    activeItemsForDebone.value = []
    return
  }
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)

    const res = await apiFetch(`/items?${params.toString()}`, { method: 'GET' })
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    activeItemsForDebone.value = (Array.isArray(data) ? data : [])
      .filter((item) => (item.status ?? 1) === 1)
      .map((item) => ({
        id: item.id,
        name: item.itemName || item.name || '-',
        // Stock unit only — do not use unitProduction for Debone U.O.M filtering
        unit: item.unit || '',
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
  } catch (err) {
    console.error('Error loading active items for debone:', err)
  }
}

function selectRegistrationTab(tab) {
  if (activeRegistrationTab.value === tab) return
  activeRegistrationTab.value = tab
  editingIndex.value = null
  productSearchQuery.value = ''
  resetForm()
  if (tab === 'debone') {
    loadActiveItemsForDebone()
    loadDeboneProducts()
  } else {
    loadProducts()
  }
}

async function loadDepartments() {
  productCostsLoading.value = true
  try {
   
    // Align with backend requirement: provide branchId or companyId via query params
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url, { method: 'GET' })
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const rawDepartments = Array.isArray(data) ? data : []

    const allowedNames = userDepartmentNames.value

    if (allowedNames.length > 0) {
      const nameSet = new Set(allowedNames)
      // Keep only departments whose name appears in the user's token `departments`
      departments.value = rawDepartments.filter(
        d => nameSet.has(d.name) && (d.status ?? 1) === 1
      )
    } else if (departmentId.value !== null && departmentId.value !== undefined && String(departmentId.value) !== '') {
      // Fallback: single primary department id if present
      const targetId = String(departmentId.value)
      departments.value = rawDepartments.filter(
        d => String(d.id) === targetId && (d.status ?? 1) === 1
      )
    } else {
      // Last fallback: all active departments in scope
      departments.value = rawDepartments.filter(d => (d.status ?? 1) === 1)
    }
    departments.value = departments.value.filter(d => d.status === 1)
    departments.value = departments.value.filter(d => {
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
  } catch (err) {
    console.error('Error loading departments:', err)
  } 
}

function handleDepartmentChange() {
  const depId = Number(form.value.departmentId || 0)
  const dep = departments.value.find(d => Number(d.id) === depId)
  subDepartments.value = Array.isArray(dep?.subDepartments) ? dep.subDepartments.filter(sd => (sd.status ?? 1) === 1) : []
  subDepartments.value.sort((a, b) => a.name.localeCompare(b.name))
  // Product tab only: clear sub-dept. Debone keeps U.O.M / item / name / quantity.
  if (activeRegistrationTab.value === 'product') {
    form.value.subDepartmentId = ''
  }
}

function mapProductToRow(p) {
  const rawUom = p?.uom ?? p?.UOM ?? p?.unit ?? p?.unitProduction ?? p?.u_o_m ?? ''
  let normalizedUom = String(rawUom || '').trim()
  // Backward compatibility for previously-saved abbreviations
  if (normalizedUom === 'Ltr') normalizedUom = 'liter'
  const recentUnitPrice = p.recent_unit_price ?? p.recentUnitPrice ?? null
  const recentVolume = p.recent_volume ?? p.recentVolume ?? null
  return {
    id: p.id,
    productName: p.name,
    departmentId: p.departmentId ?? p.department_id ?? p.department?.id,
    subDepartmentId: p.subDepartmentId ?? p.sub_department_id ?? p.subDepartment?.id,
    department: p.department?.name || '-',
    subDepartment: p.subDepartment?.name || '-',
    uom: normalizedUom,
    quantity: p.quantity,
    contingency: p.contingency ?? null,
    unitCost: null,
    recentUnitPrice,
    recentVolume,
    itemId: p.itemId ?? p.item_id ?? null,
    itemName: p.itemName || p.item_name || '',
    deboneRegistrationId: p.deboneRegistrationId ?? p.debone_registration_id ?? null,
    active: (p.status ?? 1) === 1,
  }
}

function computeDeboneUnitCost(row) {
  const rup = Number(row?.recentUnitPrice)
  const rvol = Number(row?.recentVolume)
  if (Number.isFinite(rup) && Number.isFinite(rvol) && rvol > 0) {
    return rup / rvol
  }
  return null
}

async function loadProducts() {
  productCostsLoading.value = false
  if (!companyId.value && !branchId.value) return
  productCostsLoading.value = true
  try {
    const res = await apiFetch('/products?excludeDeboneLinked=true', { method: 'GET' })
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    const allowedDeptIds = new Set(departments.value.map(d => String(d.id)))
    items.value = (Array.isArray(data) ? data.map(mapProductToRow) : [])
      .filter(row => allowedDeptIds.has(String(row?.departmentId ?? '')))
    items.value.sort((a, b) => a.productName.localeCompare(b.productName))
    await loadProductCostsForTable(items.value)
  } catch (err) {
    console.error('Error loading products:', err)
  }
}

async function loadDeboneProducts() {
  if (!companyId.value && !branchId.value) {
    deboneItems.value = []
    return
  }
  try {
    const res = await apiFetch('/products?deboneLinked=true', { method: 'GET' })
    if (!res.ok) return
    const data = await res.json().catch(() => [])
    const allowedDeptIds = new Set(departments.value.map(d => String(d.id)))
    deboneItems.value = (Array.isArray(data) ? data.map(mapProductToRow) : [])
      .filter(row => allowedDeptIds.has(String(row?.departmentId ?? '')))
      .map((row) => ({
        ...row,
        unitCost: computeDeboneUnitCost(row),
      }))
    deboneItems.value.sort((a, b) =>
      String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
      || String(a.productName || '').localeCompare(String(b.productName || ''), undefined, { sensitivity: 'base' })
    )
  } catch (err) {
    console.error('Error loading debone products:', err)
  }
}

async function loadItemsForCostingIfNeeded() {
  if (itemsForRecipe.value.length > 0) return
  await loadItemsForProductRecipe()
}

async function computeProductCostFromRecipe(recipeData, contingencyPercent = DEFAULT_PRODUCT_CONTINGENCY) {
  const ings = Array.isArray(recipeData?.ingredients) ? recipeData.ingredients : []
  let costSubTotalLocal = 0

  const normalizeUnit = (u) => String(u ?? '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '')

  const isKgOrLiter = (u) => {
    const unitNorm = normalizeUnit(u)
    return unitNorm === 'kg' || unitNorm === 'liter'
  }

  const isMlOrGram = (u) => {
    const unitNorm = normalizeUnit(u)
    return unitNorm === 'ml' || unitNorm === 'milliliter' || unitNorm === 'gram'
  }

  const isPieces = (u) => {
    const unitNorm = normalizeUnit(u)
    return unitNorm === 'piece' || unitNorm === 'pieces' || unitNorm === 'pc' || unitNorm === 'pcs'
  }

  const isBottle = (u) => {
    const unitNorm = normalizeUnit(u)
    return unitNorm === 'bottle' || unitNorm === 'bottles'
  }

  const isGlass = (u) => {
    const unitNorm = normalizeUnit(u)
    return unitNorm === 'glass' || unitNorm === 'glasses'
  }

  const computeUpFromRecentPriceAndVolume = (unit, unitProduction, recentUnitPrice, recentVolume) => {
    const up = Number(recentUnitPrice ?? 0)
    const vol = Number(recentVolume ?? 0)
    if (!(Number.isFinite(up) && Number.isFinite(vol) && vol > 0)) return null
    if (isKgOrLiter(unit) && isMlOrGram(unitProduction)) {
      return (up / vol) * 1000
    }
    return null
  }

  const computeItemModalDisplayUp = (unit, unitProduction, recentUnitPrice, recentVolume, glassAmount) => {
    const up = Number(recentUnitPrice ?? 0)
    const vol = Number(recentVolume ?? 0)
    if (!(Number.isFinite(up) && Number.isFinite(vol) && vol > 0)) return null

    if (isKgOrLiter(unit) && isMlOrGram(unitProduction)) {
      return (up / vol) * 1000
    }
    if (isKgOrLiter(unit) && isGlass(unitProduction)) {
      const g = Number(glassAmount ?? 0)
      if (Number.isFinite(g) && g > 0) return (up / vol) * g
      return null
    }

    if (isPieces(unit)) {
      if (isMlOrGram(unitProduction)) return (up / vol) * 1000
      if (isBottle(unitProduction)) return up / vol
      if (isGlass(unitProduction)) {
        const g = Number(glassAmount ?? 0)
        if (Number.isFinite(g) && g > 0) return (up / vol) * g
        return null
      }
      if (isPieces(unitProduction)) return up
    }
    return null
  }

  const getProductDisplayUom = (u) => {
    const unitNorm = normalizeUnit(u)
    if (unitNorm === 'kg') return 'Gram'
    if (unitNorm === 'liter') return 'M.L.'
    return u ?? ''
  }

  for (const i of ings) {
    if (i?.isActive === 0 || i?.isActive === '0') continue
    
    let up = 0
    let upFromFetchProductCost = false

    const type = i.type || ((i.productId || i.product_id) ? 'product' : 'item')
    const qty = Number(i.quantity || 0)

    if (type === 'product') {
      const productId = i.productId || i.product_id || i.product?.id

      // Match Recipe for modal: prefer backend recent_unit_price, fallback to fetchProductCost.
      const productRecentUp = Number(
        i.product?.recent_unit_price ??
        i.product?.recentUnitPrice ??
        i.product?.recentPrice ??
        0
      )

      up = productRecentUp
      if (!up && productId) {
        up = await fetchProductCost(productId)
        upFromFetchProductCost = true
      }
      const productUom = i.product?.uom || i.uom || ''
      const productDisplayUom = getProductDisplayUom(productUom)

      if (!upFromFetchProductCost) {
        const derived = computeUpFromRecentPriceAndVolume(
          productUom,
          productDisplayUom,
          i.product?.recent_unit_price ?? i.product?.recentUnitPrice ?? i.product?.recentPrice ?? null,
          i.product?.recent_volume ?? i.product?.recentVolume ?? null
        )
        if (derived != null) up = derived
      }

      const total = qty * up
      costSubTotalLocal += isMlOrGram(productDisplayUom) ? (total / 1000) : total
      continue
    }

    const itemId = i.itemId ?? i.item_id ?? i.item?.id
    const fullItem = itemsForRecipe.value.find(it => String(it.id) === String(itemId))
    const itemUnit = fullItem?.unit ?? i.item?.unit ?? ''
    const itemUnitProduction = fullItem?.unitProduction ?? i.item?.unitProduction ?? i.unit ?? ''
    const itemRecentUnitPrice = fullItem?.recent_unit_price ?? fullItem?.recentPrice ?? fullItem?.recentUnitPrice ?? i.item?.recent_unit_price ?? i.item?.recentPrice ?? i.item?.recentUnitPrice ?? 0
    const itemRecentVolume = fullItem?.recent_volume ?? fullItem?.recentVolume ?? i.item?.recent_volume ?? i.item?.recentVolume ?? null
    const itemGlassAmount = fullItem?.glass_amount ?? i.item?.glass_amount ?? null

    up = Number(itemRecentUnitPrice)
    const derivedItemUp = computeItemModalDisplayUp(
      itemUnit,
      itemUnitProduction,
      itemRecentUnitPrice,
      itemRecentVolume,
      itemGlassAmount
    )
    if (derivedItemUp != null) up = derivedItemUp

    const total = qty * up
    costSubTotalLocal += isMlOrGram(itemUnitProduction) ? (total / 1000) : total
  }
  const pct = Number(contingencyPercent ?? DEFAULT_PRODUCT_CONTINGENCY) / 100
  const contingency = costSubTotalLocal * pct
  const total = costSubTotalLocal + contingency
  return { totalCost: total }
}

async function loadProductCostsForTable(rows) {
  productCostsLoading.value=false
  if (!Array.isArray(rows) || rows.length === 0) return
  productCostsLoading.value = true
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    params.append('productIds', rows.map((r) => r.id).join(','))

    const res = await apiFetch(`/product-recipe-costs?${params.toString()}`, { method: 'GET' })
    if (!res.ok) {
      await loadProductCostsForTableLegacy(rows)
      return
    }

    const data = await res.json().catch(() => [])
    const costByProductId = new Map()
    for (const row of (Array.isArray(data) ? data : [])) {
      costByProductId.set(Number(row.productId), row)
    }

    for (const row of rows) {
      if (!row?.id) continue
      const entry = costByProductId.get(Number(row.id))
      if (!entry || entry.unitCost == null) {
        row.unitCost = null
      } else {
        row.unitCost = Number(entry.unitCost ?? 0)
      }
    }
  } catch (err) {
    console.error('Error loading product recipe costs:', err)
    await loadProductCostsForTableLegacy(rows)
  } finally {
    productCostsLoading.value = false
  }
}

async function loadProductCostsForTableLegacy(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return
  await loadItemsForCostingIfNeeded()

  await Promise.all(rows.map(async (row) => {
    if (!row?.id) return
    try {
      const res = await apiFetch(`/product-recipes/product/${row.id}`, { method: 'GET' })
      if (!res.ok) {
        row.unitCost = null
        return
      }
      const data = await res.json().catch(() => null)
      if (!data) {
        row.unitCost = null
        return
      }
      const { totalCost } = await computeProductCostFromRecipe(data, row.contingency ?? DEFAULT_PRODUCT_CONTINGENCY)
      const qty = Number(row.quantity || 0)
      row.unitCost = qty > 0 ? (totalCost / qty) : 0
    } catch {
      row.unitCost = null
    }
  }))
}

function normalizeUnit(u) {
  return String(u ?? '')
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '')
}

function isKgOrLiter(u) {
  const unitNorm = normalizeUnit(u)
  return unitNorm === 'kg' || unitNorm === 'liter'
}

function isMlOrGram(u) {
  const unitNorm = normalizeUnit(u)
  return unitNorm === 'ml' || unitNorm === 'milliliter' || unitNorm === 'gram'
}

function getSavedProductRecipeScale(row) {
  if (!row) return 1
  if (row.type === 'item') {
    const item = itemsForRecipe.value.find(it => String(it.id) === String(row.itemId))
    const itemUnitProduction = item?.unitProduction ?? row.uom ?? ''
    return isMlOrGram(itemUnitProduction) ? 1000 : 1
  }
  if (row.type === 'product') {
    const productUom = row.uom ?? ''
    return isKgOrLiter(productUom) ? 1000 : 1
  }
  return 1
}

function buildSavedProductRecipeIngredientAmounts(rows, numberYield) {
  const safeYield = Number(numberYield) > 0 ? Number(numberYield) : 1

  return rows.map((row) => {
    const scale = getSavedProductRecipeScale(row)
    const qty = Number(row.qty ?? row.quantity ?? 0)
    const amount = qty / safeYield / (scale || 1)
    const id = row.type === 'product'
      ? Number(row.productId || 0)
      : Number(row.itemId || 0)

    return {
      Id: id,
      amount: Number(amount.toFixed(6)),
      type: row.type === 'product' ? 'Product' : 'Item'
    }
  }).filter((entry) => Number(entry.Id) > 0)
}

async function persistSavedProductRecipeSnapshot(productId, numberYield, rows) {
  const snapshotPayload = {
    numberYield: Number(numberYield || 1),
    ingredientAndAmount: buildSavedProductRecipeIngredientAmounts(rows, numberYield)
  }
  const snapshotRes = await apiFetch(`/product-recipes/product/${productId}/saved-product-recipe`, {
    method: 'POST',
    body: JSON.stringify(snapshotPayload),
  })
  if (!snapshotRes.ok) {
    const err = await snapshotRes.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to save product recipe snapshot')
  }
}

async function persistRecentUnitPriceAndVolume(productId, costPerUnit) {
  const recentCostRes = await apiFetch('/production/product/recent-cost', {
    method: 'POST',
    body: JSON.stringify({
      productId,
      costPerUnit: Number(costPerUnit || 0),
    }),
  })
  if (!recentCostRes.ok) {
    const err = await recentCostRes.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to update product recent cost')
  }
}

const handleSubmit = async () => {
  if (activeRegistrationTab.value === 'debone') {
    await handleDeboneSubmit()
    return
  }
  if (!canAddProduct.value) return
  if (!form.value.productName || !form.value.uom || !form.value.quantity || !form.value.departmentId) return
  if (!validateRegistrationUniqueness()) return

  const payload = {
    name: form.value.productName,
    departmentId: Number(form.value.departmentId),
    subDepartmentId: form.value.subDepartmentId ? Number(form.value.subDepartmentId) : null,
    uom: String(form.value.uom || '').trim(),
    quantity: Number(form.value.quantity),
    companyId: branchId.value ? null : companyId.value,
    branchId: branchId.value || null,
  }

  // New registrations default to active; updates must preserve existing status.
  if (editingIndex.value === null) {
    payload.status = 1
  }

  try {
    if (editingIndex.value !== null) {
      const productId = editingIndex.value
      await apiFetch(`/products/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Failed to update product')
      })
      const uomChangedOnUpdate = String(form.value.uom || '').trim()
        !== String(editingRegistrationUom.value || '').trim()
      // UOM changes trigger full product + menu recipe recalc on the server (same as item UOM updates).
      if (!uomChangedOnUpdate) {
        try {
          const editingItem = items.value.find((item) => Number(item.id) === Number(productId))
          const contingency = editingItem?.contingency ?? DEFAULT_PRODUCT_CONTINGENCY
          await applyProductUpdateSideEffects(productId, Number(form.value.quantity), contingency)
        } catch (sideEffectErr) {
          console.error('Error saving product recipe side effects on update:', sideEffectErr)
          showToast(sideEffectErr.message || 'Product updated but failed to save recipe data', 'error')
        }
      }
    } else {
      await apiFetch('/products', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || 'Failed to register product')
      })
    }

    editingIndex.value = null
    resetForm()
    await loadProducts()
  } catch (err) {
    console.error(err)
    showToast(err.message || 'Failed to save product', 'error')
  }
}

async function handleDeboneSubmit() {
  if (!canAddProduct.value && editingIndex.value === null) return
  if (editingIndex.value !== null && !canEditProduct.value && !canAddProduct.value) return
  if (!form.value.productName || !form.value.uom || !form.value.departmentId || !form.value.itemId) return
  if (!validateRegistrationUniqueness()) return

  const wasEdit = editingIndex.value !== null
  const payload = {
    name: form.value.productName,
    departmentId: Number(form.value.departmentId),
    subDepartmentId: null,
    uom: String(form.value.uom || '').trim(),
    // Debone has no Quantity field — always persist 1
    quantity: 1,
    companyId: branchId.value ? null : companyId.value,
    branchId: branchId.value || null,
    debone: {
      itemId: Number(form.value.itemId),
    },
  }

  if (!wasEdit) {
    payload.status = 1
  }

  try {
    if (wasEdit) {
      const productId = editingIndex.value
      const res = await apiFetch(`/products/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to update debone')
    } else {
      const res = await apiFetch('/products', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to register debone')
    }

    editingIndex.value = null
    // Keep selected department + item after debone register/update
    form.value.productName = ''
    form.value.uom = ''
    form.value.quantity = ''
    form.value.subDepartmentId = ''
    await loadDeboneProducts()
    showToast(wasEdit ? 'Debone updated successfully' : 'Debone registered successfully', 'success')
  } catch (err) {
    console.error(err)
    showToast(err.message || 'Failed to save debone', 'error')
  }
}

const handleEdit = (index) => {
  if (!canEditProduct.value) return
  const item = tableRows.value[index]
  if (!item) return
  form.value = {
    departmentId: item.departmentId ? String(item.departmentId) : '',
    subDepartmentId: item.subDepartmentId ? String(item.subDepartmentId) : '',
    itemId: item.itemId ? String(item.itemId) : '',
    productName: item.productName || '',
    uom: item.uom || '',
    quantity: activeRegistrationTab.value === 'debone' && isDeboneNonPieceUom(item.uom)
      ? ''
      : (item.quantity ?? ''),
  }
  // Ensure sub-depts match selected department for the dropdown
  if (activeRegistrationTab.value === 'product') {
    handleDepartmentChange()
    form.value.subDepartmentId = item.subDepartmentId ? String(item.subDepartmentId) : ''
  }
  // Debone: keep loaded U.O.M / quantity / fields as-is (do not re-sync from item unit)
  editingIndex.value = item.id
  editingRegistrationUom.value = String(item.uom || '').trim()
}

const resetForm = () => {
  form.value = { departmentId: '', subDepartmentId: '', itemId: '', productName: '', uom: '', quantity: '' }
  subDepartments.value = []
  editingRegistrationUom.value = ''
}

async function toggleProductStatus(row) {
  if (!canEditProduct.value) return
  if (!row?.id) return
  const nextActive = !row.active
  row.active = nextActive
  try {
    const res = await apiFetch(`/products/${row.id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: nextActive ? 1 : 0 }),
    })
    if (!res.ok) {
      row.active = !nextActive
    }
  } catch (err) {
    console.error('Error toggling status:', err)
    row.active = !nextActive
  }
}

// Recipe Modal Logic
const showModal = ref(false)
const showContingencyModal = ref(false)
const editingContingencyIndex = ref(-1)
const contingencyValue = ref('')
const selectedProduct = ref(null)
const recipeNotes = ref('')
const editingRecipeIndex = ref(-1)

const itemsForRecipe = ref([])
const addQuantity = ref('')
const addIngredientItemId = ref('')
const addUom = ref('')
const addUP = ref(0)
const addRemark = ref('')
const recipeLoading = ref(false)
const recipeSaving = ref(false)

const recipeRows = ref([])
const deletedIngredientIds = ref([])
const productsForRecipe = ref([])
const ingredientOptions = computed(() => {
  const itemOpts = itemsForRecipe.value.filter(i => i.isIngredient === 1 || i.isIngredient === '1')
  const prodOpts = productsForRecipe.value.filter(p => Number(p.id) !== Number(selectedProduct.value?.id))
  return [...itemOpts, ...prodOpts].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})

/** Dropdown label only: products show (Debo)/(Pro); items unchanged. */
function getIngredientOptionLabel(opt) {
  if (!opt) return ''
  if (opt.type !== 'product') return opt.name || ''
  return `${opt.name || ''} (${opt.isDebone ? 'Debo' : 'Pro'})`
}

// Toast notification state (same pattern as RecipePage.vue)
const toast = ref({ show: false, message: '', type: 'success' })
function showToast(message, type = 'success') {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

// const otherRecipes = ref([
//   { id: 1, name: 'Standard Pizza Dough', ingredients: [
//     { ingredient: 'Flour', uom: 'Kg', qty: 1, up: 80, total: 80, remark: 'White' },
//     { ingredient: 'Yeast', uom: 'Kg', qty: 0.05, up: 500, total: 25, remark: 'Dry' }
//   ]}
// ])

// Calculations
const costSubTotal = computed(() => {
  return recipeRows.value.reduce((sum, r) => sum + Number(getRecipeModalDisplayTotal(r) || 0), 0)
})
const contingencyAmount = computed(() => {
  const percentage = Number(selectedProduct.value?.contingency ?? DEFAULT_PRODUCT_CONTINGENCY) / 100
  return costSubTotal.value * percentage
})
const totalCost = computed(() => costSubTotal.value + contingencyAmount.value)
const costPerUnit = computed(() => {
  const qty = Number(selectedProduct.value?.quantity || 1)
  return qty > 0 ? (totalCost.value / qty) : 0
})

async function loadItemsForProductRecipe() {
  if (!companyId.value && !branchId.value) return
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)

    const [itemsRes, productsRes, deboneLinkedRes] = await Promise.all([
      apiFetch(`/items?${params.toString()}`, { method: 'GET' }),
      apiFetch('/products', { method: 'GET' }),
      apiFetch('/products?deboneLinked=true', { method: 'GET' })
    ])

    const itemsData = await itemsRes.json().catch(() => [])
    const activeItems = Array.isArray(itemsData) ? itemsData.filter(i => (i.status ?? 1) === 1) : []
    
    // itemsForRecipe should hold ALL active items for cost calculations
    itemsForRecipe.value = activeItems.map(i => ({
      id: i.id,
      type: 'item',
      name: i.itemName,
      // Keep raw fields used by modal U.P population + unit conversions.
      unit: i.unit,
      unitProduction: i.unitProduction,
      recent_unit_price: i.recent_unit_price,
      recentPrice: i.recentPrice,
      recentUnitPrice: i.recentUnitPrice,
      recent_volume: i.recent_volume,
      recentVolume: i.recentVolume,
      glass_amount: i.glass_amount ?? i.glassAmount ?? null,
      uom: i.unitProduction || i.unit,
      up: Number(i.recent_unit_price ?? i.recentPrice ?? i.recentUnitPrice ?? 0),
      isIngredient: i.isIngredient ?? i.is_ingredient ?? 1
    }))

    const deboneLinkedIds = new Set()
    if (deboneLinkedRes.ok) {
      const deboneLinkedData = await deboneLinkedRes.json().catch(() => [])
      if (Array.isArray(deboneLinkedData)) {
        for (const p of deboneLinkedData) {
          if (p?.id != null) deboneLinkedIds.add(Number(p.id))
        }
      }
    }

    if (productsRes.ok) {
      const productsData = await productsRes.json().catch(() => [])
      const activeProducts = Array.isArray(productsData) ? productsData.filter(p => (p.status ?? 1) === 1) : []
      
      productsForRecipe.value = activeProducts.map(p => {
        const recentUp = Number(
          p.recent_unit_price ??
          p.recentUnitPrice ??
          p.recentPrice ??
          0
        )
        const opt = reactive({
          id: p.id,
          type: 'product',
          name: p.name,
          uom: p.uom,
          recent_unit_price: p.recent_unit_price ?? null,
          recent_volume: p.recent_volume ?? null,
          contingency: p.contingency ?? DEFAULT_PRODUCT_CONTINGENCY,
          // Prefer product recent_unit_price for modal U.P.
          // If missing/0, fallback to recipe-based cost via fetchProductCost().
          up: recentUp,
          quantity: Number(p.quantity || 1),
          isDebone: deboneLinkedIds.has(Number(p.id)),
        })
        if (!recentUp) {
          fetchProductCost(p.id, opt.quantity).then(cost => {
            opt.up = cost
          })
        }
        return opt
      })
    }
  } catch (err) {
    console.error('Error loading items for product recipe:', err)
  }
}

async function loadProductRecipe() {
  if (!selectedProduct.value?.id) return
  try {
    const res = await apiFetch(`/product-recipes/product/${selectedProduct.value.id}`, { method: 'GET' })
    if (!res.ok) {
      recipeRows.value = []
      recipeNotes.value = ''
      return
    }
    const data = await res.json().catch(() => null)
    if (!data) return

    recipeNotes.value = data.notes ?? ''
    // Fresh DB load clears staged deletions.
    deletedIngredientIds.value = []
    const ings = Array.isArray(data.ingredients) ? data.ingredients : []
    const rows = await buildRecipeCostRowsFromApiIngredients(ings)
    recipeRows.value = rows
  } catch (err) {
    console.error('Error loading product recipe:', err)
  }
}

async function openRecipeModal(product) {
  selectedProduct.value = product
  showModal.value = true
  recipeLoading.value = true
  try {
    if (itemsForRecipe.value.length === 0) {
      await loadItemsForProductRecipe()
    }
    await loadProductRecipe()
  } finally {
    recipeLoading.value = false
  }
}

async function saveRecipeModalChanges({ showSuccessToast = true } = {}) {
  if (!selectedProduct.value?.id) return false

  // 1) Persist recipe notes/details (existing behavior)
  const detailsRes = await apiFetch(`/product-recipes/product/${selectedProduct.value.id}/details`, {
    method: 'PUT',
    body: JSON.stringify({ notes: recipeNotes.value || '' }),
  })
  if (!detailsRes.ok) {
    const err = await detailsRes.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to save recipe details')
  }

  // 2) Persist staged deletions.
  for (const ingredientId of deletedIngredientIds.value) {
    const delRes = await apiFetch(`/product-recipes/ingredient/${ingredientId}`, { method: 'DELETE' })
    if (!delRes.ok) {
      const err = await delRes.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to delete ingredient')
    }
  }
  deletedIngredientIds.value = []

  // 3) Persist current modal ingredient rows (new + existing).
  for (const row of recipeRows.value) {
    const payload = {
      itemId: row.type === 'item' ? Number(row.itemId || 0) : null,
      productId: row.type === 'product' ? Number(row.productId || 0) : null,
      type: row.type,
      quantity: Number(row.qty || 0),
      unit: row.uom || '',
      isActive: row.isActive ? 1 : 0,
      remark: row.remark || null,
    }

    if (row.id) {
      const updateRes = await apiFetch(`/product-recipes/ingredient/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      if (!updateRes.ok) {
        const err = await updateRes.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to update ingredient')
      }
    } else {
      const addRes = await apiFetch(`/product-recipes/product/${selectedProduct.value.id}/ingredient`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to add ingredient')
      }
    }
  }

  // 4) Save current modal snapshot to saved_product_recipe (new functionality).
  await persistSavedProductRecipeSnapshot(
    selectedProduct.value.id,
    Number(selectedProduct.value?.quantity || 1),
    recipeRows.value
  )

  // 5) Persist product recent_unit_price/recent_volume (existing behavior).
  await saveRecentUnitPriceAndVolume()

  if (showSuccessToast) {
    showToast('Recipe details saved successfully', 'success')
  }
  return true
}

async function saveRecentUnitPriceAndVolume() {
  if (!selectedProduct.value?.id) return
  await persistRecentUnitPriceAndVolume(selectedProduct.value.id, costPerUnit.value)
}

const closeModal = async () => {
  // if (selectedProduct.value?.id) {
  //   await saveProductRecipeNotes()
  // }
  showModal.value = false
  resetRecipeForm()
  await loadProducts()
}

const closeModalWithSave = async () => {
  await closeModal()
}

const onIngredientSelect = async () => {
  if (!addIngredientItemId.value) return
  const [type, id] = addIngredientItemId.value.split(':')
  const opt = ingredientOptions.value.find(o => o.type === type && String(o.id) === String(id))
  
  if (opt) {
    addUom.value = opt.uom
    if (type === 'product') {
      const optUp = Number(opt.up || 0)
      addUP.value = optUp ? optUp : await fetchProductCost(opt.id, opt.quantity)
    } else {
      addUP.value = opt.up
    }
  }
}

async function fetchProductCost(pid, pQty = null) {
  if (!pid) return 0
  try {
    let yieldQty = pQty
    let contingencyPct = null

    if (!yieldQty) {
      const opt = (ingredientOptions.value || []).find(o => o.type === 'product' && String(o.id) === String(pid))
      if (opt?.quantity) {
        yieldQty = Number(opt.quantity)
      }
      if (opt?.contingency != null) {
        contingencyPct = Number(opt.contingency)
      }
    }

    if (!yieldQty || contingencyPct == null) {
      const pRes = await apiFetch(`/products/${pid}`, { method: 'GET' })
      const prodRaw = await pRes.json().catch(() => ({}))
      const prod = prodRaw.data || prodRaw
      if (!yieldQty) yieldQty = Number(prod?.quantity || 1)
      if (contingencyPct == null) contingencyPct = Number(prod?.contingency ?? DEFAULT_PRODUCT_CONTINGENCY)
    }
    if (!yieldQty) yieldQty = 1
    if (contingencyPct == null || Number.isNaN(contingencyPct)) {
      contingencyPct = DEFAULT_PRODUCT_CONTINGENCY
    }

    // 2. Get the target product's recipe
    const res = await apiFetch(`/product-recipes/product/${pid}`, { method: 'GET' })
    if (!res.ok) return 0
    const data = await res.json().catch(() => null)
    if (!data || !data.ingredients || data.ingredients.length === 0) return 0

    const getProductDisplayUom = (u) => {
      const unitNorm = normalizeUnit(u)
      if (unitNorm === 'kg') return 'Gram'
      if (unitNorm === 'liter') return 'M.L.'
      return u ?? ''
    }

    // 3. Sum up ingredients using same Unit Cost logic
    let subtotal = 0
    for (const ing of data.ingredients) {
      if (ing.isActive === 0 || ing.isActive === '0') continue
      
      const type = ing.type || ((ing.productId || ing.product_id || ing.product?.id) ? 'product' : 'item')
      let up = 0

      if (type === 'product') {
        const nestedPid = ing.productId || ing.product_id || ing.product?.id
        const nestedRecentUp = Number(
          ing.product?.recent_unit_price ??
          ing.product?.recentUnitPrice ??
          ing.product?.recentPrice ??
          0
        )

        up = nestedRecentUp
        let fromFallback = false
        if (!up && nestedPid && String(nestedPid) !== String(pid)) {
          up = await fetchProductCost(nestedPid)
          fromFallback = true
        }

        const productUom = ing.product?.uom || ing.unit || ''
        const productDisplayUom = getProductDisplayUom(productUom)
        if (!fromFallback) {
          const derived = computeUpFromRecentPriceAndVolume(
            productUom,
            productDisplayUom,
            ing.product?.recent_unit_price ?? ing.product?.recentUnitPrice ?? ing.product?.recentPrice ?? null,
            ing.product?.recent_volume ?? ing.product?.recentVolume ?? null
          )
          if (derived != null) up = derived
        }

        const total = Number(ing.quantity || 0) * up
        subtotal += isMlOrGram(productDisplayUom) ? (total / 1000) : total
      } else {
        const itemId = ing.itemId || ing.item_id
        const fullItem = itemsForRecipe.value.find(it => String(it.id) === String(itemId))
        const itemUnit = fullItem?.unit ?? ing.item?.unit ?? ''
        const itemUnitProduction = fullItem?.unitProduction ?? ing.item?.unitProduction ?? ing.unit ?? ''
        const itemRecentUnitPrice = (
          fullItem?.recent_unit_price ??
          fullItem?.recentPrice ??
          fullItem?.recentUnitPrice ??
          ing.item?.recent_unit_price ??
          ing.item?.recentPrice ??
          ing.item?.recentUnitPrice ??
          0
        )
        const itemRecentVolume = (
          fullItem?.recent_volume ??
          fullItem?.recentVolume ??
          ing.item?.recent_volume ??
          ing.item?.recentVolume ??
          null
        )
        const itemGlassAmount = (
          fullItem?.glass_amount ??
          fullItem?.glassAmount ??
          ing.item?.glass_amount ??
          ing.item?.glassAmount ??
          null
        )

        up = Number(itemRecentUnitPrice)
        const derivedItemUp = computeItemModalDisplayUp(
          itemUnit,
          itemUnitProduction,
          itemRecentUnitPrice,
          itemRecentVolume,
          itemGlassAmount
        )
        if (derivedItemUp != null) up = derivedItemUp

        const total = Number(ing.quantity || 0) * up
        subtotal += isMlOrGram(itemUnitProduction) ? (total / 1000) : total
      }
    }

    const totalWithContingency = subtotal * (1 + contingencyPct / 100)
    return yieldQty > 0 ? (totalWithContingency / yieldQty) : 0
  } catch (err) {
    console.error(`Error fetching cost for product ${pid}:`, err)
    return 0
  }
}

async function buildRecipeCostRowsFromApiIngredients(ings) {
  return Promise.all(ings.map(async (i) => {
    const itemId = i.itemId ?? i.item_id ?? i.item?.id ?? null
    const productId = i.productId ?? i.product_id ?? i.product?.id ?? null
    const type = i.type || (productId ? 'product' : 'item')

    let up = 0
    let upFromFetchProductCost = false
    let ingredientName = ''
    let uom = ''

    if (type === 'item') {
      const fullItem = itemsForRecipe.value.find(it => String(it.id) === String(itemId))
      up = Number(fullItem?.recent_unit_price ?? fullItem?.recentPrice ?? fullItem?.recentUnitPrice ?? i.item?.recent_unit_price ?? i.item?.recentPrice ?? i.item?.recentUnitPrice ?? 0)
      ingredientName = i.item?.itemName || i.itemName || ''
      uom = fullItem?.unitProduction || i.item?.unitProduction || i.unit || ''
    } else {
      ingredientName = i.product?.name || i.productName || 'Product'
      uom = i.product?.uom || i.unit || i.uom || ''
      const productRecentUp = Number(
        i.product?.recent_unit_price ??
        i.product?.recentUnitPrice ??
        i.product?.recentPrice ??
        0
      )
      up = productRecentUp
      if (!up && productId) {
        up = await fetchProductCost(productId)
        upFromFetchProductCost = true
      }
    }

    const qty = Number(i.quantity || 0)
    return {
      id: i.id,
      itemId,
      productId,
      type,
      ingredient: ingredientName,
      uom,
      qty,
      up,
      total: qty * up,
      remark: i.remark || '',
      isActive: i.isActive ?? 1,
      upFromFetchProductCost,
      recent_unit_price: type === 'product'
        ? (i.product?.recent_unit_price ?? i.product?.recentUnitPrice ?? i.product?.recentPrice ?? null)
        : null,
      recent_volume: type === 'product'
        ? (i.product?.recent_volume ?? i.product?.recentVolume ?? null)
        : null,
    }
  }))
}

async function saveIngredient() {
  if (!canSubmitRecipeIngredient.value) return
  if (!addIngredientItemId.value || !addQuantity.value) return
  if (!selectedProduct.value?.id) return

  const [type, id] = addIngredientItemId.value.split(':')
  const selected = ingredientOptions.value.find(opt => opt.type === type && String(opt.id) === String(id))
  if (!selected) return

  const rowData = {
    itemId: type === 'item' ? Number(id) : null,
    productId: type === 'product' ? Number(id) : null,
    type,
    ingredient: selected.name,
    uom: addUom.value,
    qty: Number(addQuantity.value),
    up: Number(addUP.value || 0),
    total: Number(addQuantity.value) * Number(addUP.value || 0),
    remark: addRemark.value || '',
    isActive: 1,
    upFromFetchProductCost: type === 'product',
    recent_unit_price: type === 'product' ? Number(selected.recent_unit_price ?? selected.recentUnitPrice ?? 0) : null,
    recent_volume: type === 'product' ? Number(selected.recent_volume ?? selected.recentVolume ?? 0) : null,
  }

  const duplicateIndex = recipeRows.value.findIndex((r, idx) => {
    if (editingRecipeIndex.value !== -1 && idx === editingRecipeIndex.value) return false
    return (type === 'item' && r.itemId === rowData.itemId) ||
      (type === 'product' && r.productId === rowData.productId)
  })
  if (duplicateIndex !== -1) {
    showToast('This ingredient has already been added to the recipe.', 'error')
    return
  }

  if (editingRecipeIndex.value === -1) {
    recipeRows.value.push({
      ...rowData,
      id: null,
    })
  } else {
    const existing = recipeRows.value[editingRecipeIndex.value]
    if (!existing) return
    recipeRows.value[editingRecipeIndex.value] = {
      ...existing,
      ...rowData,
      id: existing.id ?? null,
      recent_unit_price: rowData.recent_unit_price ?? existing.recent_unit_price ?? null,
      recent_volume: rowData.recent_volume ?? existing.recent_volume ?? null,
    }
  }

  resetRecipeForm()
}

const editRecipeRow = (index) => {
  if (!canEditProduct.value) return
  const row = recipeRows.value[index]
  addIngredientItemId.value = row.type + ':' + (row.itemId || row.productId)
  addQuantity.value = row.qty
  addUom.value = row.uom
  addUP.value = row.up
  addRemark.value = row.remark
  editingRecipeIndex.value = index
}

async function deleteRecipeRow(index) {
  if (!canEditProduct.value) return
  const row = recipeRows.value[index]
  if (!row) return

  if (row.id) {
    if (!deletedIngredientIds.value.includes(row.id)) {
      deletedIngredientIds.value.push(row.id)
    }
  }
  recipeRows.value.splice(index, 1)
}

// const loadIngredientsFromOther = () => {
//   const other = otherRecipes.value.find(r => r.id === Number(selectedOtherRecipeId.value))
//   if (other) {
//     other.ingredients.forEach(ing => recipeRows.value.push({ ...ing }))
//     selectedOtherRecipeId.value = ''
//   }
// }

const resetRecipeForm = () => {
  addIngredientItemId.value = ''
  addQuantity.value = ''
  addUom.value = ''
  addUP.value = 0
  addRemark.value = ''
  editingRecipeIndex.value = -1
}

async function updateIngredientActive(row) {
  if (!row.id) return
  try {
    const res = await apiFetch(`/product-recipes/ingredient/${row.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: row.isActive ? 1 : 0 }),
    })
    if (!res.ok) {
      row.isActive = !row.isActive
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to update ingredient status', 'error')
    }
  } catch (err) {
    console.error('Error updating product ingredient active:', err)
    row.isActive = !row.isActive
    showToast(err.message || 'Failed to update ingredient status', 'error')
  }
}

function toggleActive(row) {
  if (!canEditProduct.value) return
  row.isActive = !row.isActive
  updateIngredientActive(row)
}

async function saveProductRecipeNotes() {
  if (!canSaveRecipeNotes.value) return
  if (!selectedProduct.value?.id) return
  recipeSaving.value = true
  try {
    await saveRecipeModalChanges({ showSuccessToast: true })
  } catch (err) {
    console.error('Error saving product recipe notes:', err)
    showToast(err.message || 'Failed to save recipe details', 'error')
  } finally {
    recipeSaving.value = false
  }
}

function money(val) {
  if (val === null || val === undefined || val === '') return ''
  const num = Number(val || 0)
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isPieces(u) {
  const unitNorm = normalizeUnit(u)
  return unitNorm === 'piece' || unitNorm === 'pieces' || unitNorm === 'pc' || unitNorm === 'pcs'
}

function isBottle(u) {
  const unitNorm = normalizeUnit(u)
  return unitNorm === 'bottle' || unitNorm === 'bottles' || unitNorm === 'btl'
}

function isGlass(u) {
  const unitNorm = normalizeUnit(u)
  return unitNorm === 'glass' || unitNorm === 'glasses'
}

function computeUpFromRecentPriceAndVolume(unit, unitProduction, recentUnitPrice, recentVolume) {
  const up = Number(recentUnitPrice ?? 0)
  const vol = Number(recentVolume ?? 0)
  if (!(Number.isFinite(up) && Number.isFinite(vol) && vol > 0)) return null

  if (isKgOrLiter(unit) && isMlOrGram(unitProduction)) {
    return (up / vol) * 1000
  }
  return null
}

function computeItemModalDisplayUp(unit, unitProduction, recentUnitPrice, recentVolume, glassAmount) {
  const up = Number(recentUnitPrice ?? 0)
  const vol = Number(recentVolume ?? 0)
  if (!(Number.isFinite(up) && Number.isFinite(vol) && vol > 0)) return null

  if (isKgOrLiter(unit) && isMlOrGram(unitProduction)) {
    return (up / vol) * 1000
  }
  if (isKgOrLiter(unit) && isGlass(unitProduction)) {
    const g = Number(glassAmount ?? 0)
    if (Number.isFinite(g) && g > 0) return (up / vol) * g
    return null
  }
  if (isPieces(unit)) {
    if (isMlOrGram(unitProduction)) return (up / vol) * 1000
    if (isBottle(unitProduction)) return up / vol
    if (isGlass(unitProduction)) {
      const g = Number(glassAmount ?? 0)
      if (Number.isFinite(g) && g > 0) return (up / vol) * g
      return null
    }
    if (isPieces(unitProduction)) return up
  }
  return null
}

// Modal-only display conversions to align with KG/liter pricing representation.
// Does NOT mutate row.up / row.total; used only for what is shown and for modal subtotals.
function getRecipeModalDisplayUp(row) {
  if (!row) return 0
  const baseUp = Number(row.up || 0)

  if (row.type === 'item') {
    const item = itemsForRecipe.value.find(it => String(it.id) === String(row.itemId))
    const itemUnit = item?.unit ?? ''
    const itemUnitProduction = item?.unitProduction ?? row.uom ?? ''
    const itemGlassAmount = item?.glass_amount ?? item?.glassAmount ?? null
    const derived = computeItemModalDisplayUp(
      itemUnit,
      itemUnitProduction,
      item?.recent_unit_price ?? item?.recentPrice ?? item?.recentUnitPrice ?? null,
      item?.recent_volume ?? item?.recentVolume ?? null,
      itemGlassAmount
    )
    return derived != null ? derived : baseUp
  }

  if (row.type === 'product') {
    if (row.upFromFetchProductCost) return baseUp

    const opt = ingredientOptions.value.find(o => o.type === 'product' && String(o.id) === String(row.productId))
    const productUnit = row.uom || opt?.uom || ''
    const productUnitProduction = getRecipeModalDisplayUom(row)
    const derived = computeUpFromRecentPriceAndVolume(
      productUnit,
      productUnitProduction,
      row.recent_unit_price ?? opt?.recent_unit_price ?? null,
      row.recent_volume ?? opt?.recent_volume ?? null
    )
    return derived != null ? derived : baseUp
  }

  return baseUp
}

// Display-only Total Price conversion for the "Recipe for" modal.
// Rule:
// - Only for product rows where U.P was calculated via fetchProductCost (upFromFetchProductCost = true)
// - If product U.O.M is kg/liter => display total as row.total / 1000
// Note: does NOT mutate row.total.
function getRecipeModalDisplayTotal(row) {
  if (!row) return 0

  const displayUom = getRecipeModalDisplayUom(row)
  const shouldDivideBy1000 = isMlOrGram(displayUom)

  if (!(row.type === 'product' && row.upFromFetchProductCost)) {
    const qty = Number(row.qty || 0)
    const total = qty * Number(getRecipeModalDisplayUp(row) || 0)
    return shouldDivideBy1000 ? total / 1000 : total
  }

  const baseTotal = Number(row.total || 0)
  return shouldDivideBy1000 ? baseTotal / 1000 : baseTotal
}

// Requirement: for product rows only, show "Gram" / "M.L." instead of "K.G." / "liter".
function getRecipeModalDisplayUom(row) {
  if (!row) return ''
  if (row.type !== 'product') return row.uom ?? ''

  const unitNorm = normalizeUnit(row.uom)
  if (unitNorm === 'kg') return 'Gram'
  if (unitNorm === 'liter') return 'M.L.'

  return row.uom ?? ''
}

function computeModalCostPerUnitFromRows(rows, quantity, contingencyPercent = DEFAULT_PRODUCT_CONTINGENCY) {
  const costSubTotal = rows.reduce((sum, row) => sum + Number(getRecipeModalDisplayTotal(row) || 0), 0)
  const percentage = Number(contingencyPercent ?? DEFAULT_PRODUCT_CONTINGENCY) / 100
  const totalCost = costSubTotal + (costSubTotal * percentage)
  const qty = Number(quantity || 1)
  return qty > 0 ? totalCost / qty : 0
}

async function applyProductUpdateSideEffects(productId, quantity, contingency) {
  if (!productId) return

  await loadItemsForCostingIfNeeded()
  if (itemsForRecipe.value.length === 0) {
    await loadItemsForProductRecipe()
  }

  const res = await apiFetch(`/product-recipes/product/${productId}`, { method: 'GET' })
  if (!res.ok) return

  const data = await res.json().catch(() => null)
  const ings = Array.isArray(data?.ingredients) ? data.ingredients : []
  const rows = await buildRecipeCostRowsFromApiIngredients(ings)

  await persistSavedProductRecipeSnapshot(productId, quantity, rows)
  const nextCostPerUnit = computeModalCostPerUnitFromRows(rows, quantity, contingency)
  await persistRecentUnitPriceAndVolume(productId, nextCostPerUnit)
}

function openContingencyModal(row) {
  if (!canEditProduct.value) return
  const idx = items.value.findIndex(item => Number(item.id) === Number(row.id))
  if (idx === -1) return
  editingContingencyIndex.value = idx
  contingencyValue.value = String(items.value[idx].contingency ?? DEFAULT_PRODUCT_CONTINGENCY)
  showContingencyModal.value = true
}

function closeContingencyModal() {
  showContingencyModal.value = false
  editingContingencyIndex.value = -1
  contingencyValue.value = ''
}

async function saveContingency() {
  if (!canEditProduct.value) return
  if (editingContingencyIndex.value === -1) return

  const idx = editingContingencyIndex.value
  const product = items.value[idx]
  const newContingency = Number(contingencyValue.value)
  if (Number.isNaN(newContingency)) {
    showToast('Please enter a valid contingency value', 'error')
    return
  }

  try {
    const res = await apiFetch(`/products/${product.id}`, {
      method: 'PUT',
      body: JSON.stringify({ contingency: newContingency }),
    })
    if (!res.ok) {
      showToast('Failed to update contingency', 'error')
      return
    }

    product.contingency = newContingency
    if (selectedProduct.value && Number(selectedProduct.value.id) === Number(product.id)) {
      selectedProduct.value.contingency = newContingency
    }

    if (product.unitCost != null) {
      try {
        await applyProductUpdateSideEffects(product.id, Number(product.quantity || 1), newContingency)
      } catch (sideEffectErr) {
        console.error('Error updating product costs after contingency change:', sideEffectErr)
      }
    }

    await loadProductCostsForTable([product])
    showToast('Contingency updated successfully', 'success')
    closeContingencyModal()
  } catch (err) {
    console.error('Error updating contingency:', err)
    showToast('Error updating contingency', 'error')
  }
}

function disconnectProductSyncRealtime() {
  if (productSyncReconnectTimer) {
    clearTimeout(productSyncReconnectTimer)
    productSyncReconnectTimer = null
  }
  if (productSyncSocket) {
    try {
      productSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    productSyncSocket = null
  }
}

function connectProductSyncRealtime() {
  disconnectProductSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  productSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('product.') || message.event?.startsWith('debone_registration.')) {
        if (activeRegistrationTab.value === 'debone') {
          loadDeboneProducts()
        } else {
          loadProducts()
        }
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    productSyncSocket = null
    productSyncReconnectTimer = setTimeout(() => {
      connectProductSyncRealtime()
    }, 3000)
  })
}

onMounted(async () => {
  await loadDepartments()
  await loadProducts()
  connectProductSyncRealtime()
})

onUnmounted(() => {
  disconnectProductSyncRealtime()
})
</script>

<style scoped>
/* Main Table Styles */
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

/* Modal styles */
.recipe-modal {
  display: block;
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 16px;
}

.recipe-modal-content {
  background-color: white;
  margin: 5% auto;
  padding: 24px;
  width: 80%;
  max-height: 85vh;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow-y: auto;
}

.close-modal {
  position: absolute;
  top: 15px;
  right: 20px;
  font-size: 24px;
  cursor: pointer;
  font-weight: bold;
  color: #334155;
  line-height: 1;
}

.close-modal:hover {
  color: #0f172a;
}

#modalTitle {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.recipe-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

.recipe-table th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.recipe-table td {
  padding: 8px 10px;
}

/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}
.status-badge.active {
  background: #dcfce7;
  color: #16a34a;
}
.status-badge.inactive {
  background: #fee2e2;
  color: #dc2626;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
  flex-shrink: 0;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background-color: #ef4444;
  border-radius: 15px;
  transition: 0.25s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  height: 9px;
  width: 9px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.25s;
}
.toggle-switch input:checked + .toggle-slider {
  background-color: #16a34a;
}
.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}

/* Allow programmatic toggle (modal) without checkbox */
.toggle-slider.active {
  background-color: #16a34a;
}
.toggle-slider.active::before {
  transform: translateX(13px);
}

/* Toast Transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

</style>
